import React, {useMemo, useState} from "react";
import {Alert, Badge, Button, Form, Modal, ProgressBar, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {adminAuditStaleAssignments} from "../../http/bookAPI";

const money = (value, prefix = "") => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? `${prefix}${number.toFixed(prefix ? 2 : 0)}` : `${prefix}0`;
};

const formatDate = (value, startRevDate) => {
    const startMin = Number(startRevDate);
    const date = Number.isFinite(startMin) && startMin > 0
        ? new Date(startMin * 60 * 1000)
        : (value ? new Date(value) : null);
    if (!date || Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const actionVariant = (action) => {
    if (action === "canceled") return "danger";
    if (action === "would_cancel") return "warning";
    return "secondary";
};

const AdminStaleAssignmentAudit = ({onApplied}) => {
    const [show, setShow] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [olderThanDays, setOlderThanDays] = useState(180);
    const [limit, setLimit] = useState(100);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({count: 0, refundVirt: 0, refundUsd: 0});
    const [lastMode, setLastMode] = useState(null);

    const percent = loading ? 45 : (rows.length ? 100 : 0);
    const summary = useMemo(() => totals || {count: 0, refundVirt: 0, refundUsd: 0}, [totals]);

    const resetAuditState = () => {
        setRows([]);
        setTotals({count: 0, refundVirt: 0, refundUsd: 0});
        setLastMode(null);
    };

    const handleClose = () => {
        if (loading) return;
        resetAuditState();
        setShow(false);
    };

    const runAudit = async () => {
        const days = Math.max(1, Number(olderThanDays) || 180);
        const maxRows = Math.max(1, Number(limit) || 100);
        setLoading(true);
        try {
            const result = await adminAuditStaleAssignments({
                dryRun,
                olderThanDays: days,
                limit: maxRows,
            });
            setRows(Array.isArray(result.rows) ? result.rows : []);
            setTotals(result.totals || {count: 0, refundVirt: 0, refundUsd: 0});
            setLastMode(result.applied ? "apply" : "dry");
            if (result.applied && typeof onApplied === "function") onApplied();
            if (result.applied) {
                toast.success(`Canceled ${result.totals?.count || 0} stale assignments.`);
            } else if (!result.rows?.length) {
                toast.info("No stale NEW assignments found.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to audit stale assignments.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="outline-danger" size="sm" onClick={() => setShow(true)}>
                A
            </Button>

            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                top
                dialogClassName="stale-assignment-audit-modal"
            >
                <Modal.Header closeButton={!loading} className="py-2 px-3">
                    <Modal.Title className="h5 mb-0">Stale Assignment Audit</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-3 px-3">
                    <Alert variant="light" className="border small py-2 px-3 mb-3">
                        Finds NEW assignments older than the selected age. Dry run only lists candidates; apply mode cancels them as ADM_CANCEL and returns publisher Virt plus USD book/product compensation.
                    </Alert>

                    <div className="d-flex flex-wrap align-items-end gap-2 mb-2 small">
                        <Form.Check
                            type="switch"
                            id="stale-assignment-dry-run"
                            label="Dry run"
                            checked={dryRun}
                            disabled={loading}
                            onChange={(e) => setDryRun(e.target.checked)}
                        />
                        <Form.Group style={{width: 140}}>
                            <Form.Label className="small mb-1">Older than, days</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={3650}
                                value={olderThanDays}
                                disabled={loading}
                                onChange={(e) => setOlderThanDays(Number(e.target.value) || 180)}
                            />
                        </Form.Group>
                        <Form.Group style={{width: 100}}>
                            <Form.Label className="small mb-1">Limit</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={1000}
                                value={limit}
                                disabled={loading}
                                onChange={(e) => setLimit(Number(e.target.value) || 100)}
                            />
                        </Form.Group>
                        <Button variant={dryRun ? "success" : "danger"} size="sm" disabled={loading} onClick={runAudit}>
                            {loading ? <Spinner size="sm" /> : dryRun ? "Start Dry Run" : "Start and Apply"}
                        </Button>
                    </div>

                    <ProgressBar now={percent} label={loading ? "Running..." : `${summary.count || 0} found`} className="mb-2" style={{height: 18}} />

                    <div className="d-flex flex-wrap gap-2 mb-2 small">
                        <Badge bg={lastMode === "apply" ? "danger" : "warning"} text={lastMode === "apply" ? undefined : "dark"}>
                            {lastMode === "apply" ? "Canceled" : "Candidates"}: {summary.count || 0}
                        </Badge>
                        <Badge bg="secondary">Refund Virt: {money(summary.refundVirt)}V</Badge>
                        <Badge bg="secondary">Refund USD: {money(summary.refundUsd, "$")}</Badge>
                    </div>

                    <div className="stale-assignment-audit-table-wrap">
                        <Table striped bordered hover size="sm" className="small align-middle mb-0 stale-assignment-audit-table">
                            <thead>
                            <tr>
                                <th>AID</th>
                                <th>Created</th>
                                <th>Age</th>
                                <th>Owner</th>
                                <th>Book</th>
                                <th>Type</th>
                                <th>Refund</th>
                                <th>Action</th>
                                <th>Note</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.length ? rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td>{formatDate(row.createdAt, row.startRevDate)}</td>
                                    <td>{row.ageDays ?? "-"}d</td>
                                    <td>#{row.ownerId} {row.ownerName || ""}</td>
                                    <td className="stale-assignment-book-cell">#{row.bookId} {row.bookTitle || "-"}</td>
                                    <td>{row.assTypeName || row.assTypeId || "-"}</td>
                                    <td>{money(row.refundVirt)}V / {money(row.refundUsd, "$")}</td>
                                    <td><Badge bg={actionVariant(row.action)}>{row.action}</Badge></td>
                                    <td className="stale-assignment-note-cell">{row.note || "-"}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={9} className="text-center text-muted">Run audit to load stale NEW assignments.</td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="py-2 px-3">
                    <Button variant="secondary" className="btn-sm" disabled={loading} onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
            <style>{`
                .stale-assignment-audit-modal {
                    max-width: min(1540px, calc(100vw - 96px));
                    width: min(1540px, calc(100vw - 96px));
                }
                .stale-assignment-audit-table-wrap {
                    max-height: 62vh;
                    overflow: auto;
                }
                .stale-assignment-audit-table {
                    font-size: 0.74rem;
                    table-layout: fixed;
                }
                .stale-assignment-audit-table th,
                .stale-assignment-audit-table td {
                    padding: 0.16rem 0.26rem;
                    line-height: 1.08;
                    vertical-align: middle;
                    word-break: break-word;
                }
                .stale-assignment-audit-table .badge {
                    font-size: 0.63rem;
                    padding: 0.18rem 0.34rem;
                    white-space: normal;
                }
                .stale-assignment-audit-table th:nth-child(1) { width: 56px; }
                .stale-assignment-audit-table th:nth-child(2) { width: 92px; }
                .stale-assignment-audit-table th:nth-child(3) { width: 52px; }
                .stale-assignment-audit-table th:nth-child(4) { width: 128px; }
                .stale-assignment-audit-table th:nth-child(5) { width: 38%; }
                .stale-assignment-audit-table th:nth-child(6) { width: 122px; }
                .stale-assignment-audit-table th:nth-child(7) { width: 92px; }
                .stale-assignment-audit-table th:nth-child(8) { width: 104px; }
                .stale-assignment-audit-table th:nth-child(9) { width: 170px; }
                .stale-assignment-book-cell {
                    font-size: 0.72rem;
                }
                .stale-assignment-note-cell {
                    font-size: 0.7rem;
                }
            `}</style>
        </>
    );
};

export default AdminStaleAssignmentAudit;
